import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { isEmpty } from 'lodash';

import ReimbursementWorklistApi from '../../../../api/patient/ReimbursementWorklistApi';
import { muiDataGridLocaleText } from '../../../../services/i18n/mui';
import { useEuiFiltersState, withEuiFiltersProvider } from '../../../eui/EuiFiltersContext/EuiFiltersContext';
import useDataGridOnStateChange from '../../../eui/EuiFiltersContext/hooks/useDataGridOnStateChange';
import { FRS_READY } from '../../../eui/EuiFiltersContext/reducer';

import FiltersSection from './FiltersSection/FiltersSection';
import useDataGrid from './useDataGrid';

import './ReimbursementWorklist.scss';

function ReimbursementWorklist() {
  const onceLoaded = useRef(false);
  const alreadyLoaded = useRef(false);
  const { schema, statuses, ssu, assigned, dataGridState } = useEuiFiltersState();
  const onStateChange = useDataGridOnStateChange();
  const [columns, gridData, setGridData] = useDataGrid();

  const [loading, setLoading] = useState(false);

  const requestObject = useMemo(
    function() {
      if (schema?.status !== FRS_READY || isEmpty(statuses?.value) || isEmpty(ssu) || isEmpty(assigned?.value)) {
        return;
      }
      const { studySites, selectedStudyMap, selectedSiteMap } = ssu;
      const ssuIds = studySites.reduce(function(accumulator, { id, study, site }) {
        if (selectedStudyMap[study.id] && selectedSiteMap[site.id]) {
          accumulator.push(id);
        }
        return accumulator;
      }, []);
      alreadyLoaded.current = false;
      return {
        statuses: statuses.value.map(status => status.id),
        ssuIds,
        assigneeIds: assigned.value.map(a => a.id)
      };
    },
    [schema, statuses, ssu, assigned]
  );

  const loadTableData = useCallback(
    function(request) {
      setLoading(true);
      ReimbursementWorklistApi.getReimbursementsForWorklist(request)
        .then(({ data }) => {
          setGridData(data);
          alreadyLoaded.current = true;
        })
        .finally(() => setLoading(false));
    },
    [setGridData]
  );

  const applyButton = useCallback(
    function() {
      if (requestObject) {
        loadTableData(requestObject);
      }
    },
    [loadTableData, requestObject]
  );

  useEffect(
    function() {
      if (onceLoaded.current || !requestObject) {
        return;
      }
      loadTableData(requestObject);
      onceLoaded.current = true;
    },
    [loadTableData, requestObject]
  );

  const loadingAllowed = !alreadyLoaded.current && !loading;
  const dataGridStateReady = schema?.dataGridState?.status === FRS_READY;

  return (
    <div className="eui-reimbursement-worklist">
      <FiltersSection gridData={gridData} applyButton={applyButton} loadingAllowed={loadingAllowed} />
      {dataGridStateReady && (
        <DataGridPremium
          initialState={dataGridState}
          onStateChange={onStateChange}
          slotProps={{
            toolbar: {
              printOptions: { disableToolbarButton: true },
              excelOptions: { disableToolbarButton: true },
              quickFilterProps: {
                debounceMs: 500
              }
            }
          }}
          loading={loading}
          rowHeight={50}
          rows={gridData}
          columns={columns}
          disableRowSelectionOnClick
          localeText={muiDataGridLocaleText({ noRowsLabel: 'No results to display' })}
          disableRowGrouping
          disableAggregation
          disablePivoting
          showToolbar
        />
      )}
    </div>
  );
}

export default withEuiFiltersProvider(ReimbursementWorklist, { cachePrefix: 'RWF' });
